import React, { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Upload, 
  X, 
  Play, 
  Pause, 
  Volume2, 
  VolumeX,
  RotateCcw,
  Crop,
  Palette,
  Sparkles,
  Check,
  AlertCircle,
  Image as ImageIcon,
  Video,
  Music,
  File,
  Cloud,
  Zap,
  Download,
  Share2,
  Copy,
  Trash2,
  Eye,
  EyeOff,
  Filter,
  Maximize2,
  Minimize2,
  Settings,
  Camera,
  Mic,
  FileText,
  Archive,
  Link2,
  Heart,
  Star,
  Tag,
  Folder,
  Grid,
  List
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

export interface MediaFile {
  id: string;
  file: File;
  preview: string;
  type: 'image' | 'video' | 'audio' | 'document';
  name: string;
  size: number;
  uploadProgress: number;
  uploaded: boolean;
  error?: string;
  metadata?: {
    title?: string;
    description?: string;
    tags?: string[];
    category?: string;
    visibility?: 'public' | 'private' | 'friends';
    transitionEffect?: 'fade' | 'slide' | 'zoom' | 'flip';
    role?: 'primary' | 'music';
  };
  filters?: {
    brightness: number;
    contrast: number;
    saturation: number;
    blur: number;
    hue: number;
    sepia: number;
    grayscale: number;
  };
  videoOptions?: {
    autoplay: boolean;
    muted: boolean;
    loop: boolean;
    controls: boolean;
  };
}

export interface MediaUploaderConfig {
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  acceptedTypes?: string[];
  uploadPath?: string;
  showPreview?: boolean;
  showFilters?: boolean;
  showMetadata?: boolean;
  allowMultiple?: boolean;
  showProgress?: boolean;
  autoUpload?: boolean;
  title?: string;
  description?: string;
  mode?: 'compact' | 'full' | 'inline';
  aspectRatio?: 'free' | '1:1' | '16:9' | '4:3' | '3:2';
  quality?: 'low' | 'medium' | 'high' | 'original';
  storyMode?: boolean;
}

interface UniversalMediaUploaderProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: MediaFile[]) => Promise<void>;
  onFilesChange?: (files: MediaFile[]) => void;
  config: MediaUploaderConfig;
  initialFiles?: MediaFile[];
}

const DEFAULT_CONFIG: MediaUploaderConfig = {
  maxFiles: 10,
  maxFileSize: 50 * 1024 * 1024, // 50MB
  acceptedTypes: ['image/*', 'video/*', 'audio/*'],
  showPreview: true,
  showFilters: true,
  showMetadata: true,
  allowMultiple: true,
  showProgress: true,
  autoUpload: false,
  mode: 'full',
  aspectRatio: 'free',
  quality: 'high'
};

const DEFAULT_FILTERS = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  blur: 0,
  hue: 0,
  sepia: 0,
  grayscale: 0
};

const DEFAULT_VIDEO_OPTIONS = {
  autoplay: false,
  muted: true,
  loop: false,
  controls: true
};

type StoryTransitionEffect = 'fade' | 'slide' | 'zoom' | 'flip';

const STORY_TRANSITION_OPTIONS: Array<{ value: StoryTransitionEffect; label: string; description: string }> = [
  { value: 'fade', label: 'Fade', description: 'Soft blend between photos' },
  { value: 'slide', label: 'Slide', description: 'Horizontal swipe reveal' },
  { value: 'zoom', label: 'Zoom', description: 'Cinematic push-in feel' },
  { value: 'flip', label: 'Flip', description: 'Card-style perspective turn' },
];

const STORY_FILTER_PRESETS: Array<{ id: string; label: string; values: MediaFile['filters'] }> = [
  {
    id: 'natural',
    label: 'Natural',
    values: { brightness: 102, contrast: 104, saturation: 103, blur: 0, hue: 0, sepia: 0, grayscale: 0 },
  },
  {
    id: 'cinematic',
    label: 'Cinematic',
    values: { brightness: 92, contrast: 124, saturation: 94, blur: 0, hue: -6, sepia: 12, grayscale: 4 },
  },
  {
    id: 'sunset',
    label: 'Sunset Glow',
    values: { brightness: 110, contrast: 108, saturation: 132, blur: 0, hue: -16, sepia: 18, grayscale: 0 },
  },
  {
    id: 'mono',
    label: 'Mono Mood',
    values: { brightness: 98, contrast: 116, saturation: 70, blur: 0, hue: 0, sepia: 0, grayscale: 55 },
  },
];

export function UniversalMediaUploader({
  isOpen,
  onClose,
  onUpload,
  onFilesChange,
  config,
  initialFiles = []
}: UniversalMediaUploaderProps) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const { toast } = useToast();
  
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState<MediaFile[]>(initialFiles);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState('upload');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [uploading, setUploading] = useState(false);
  const [globalProgress, setGlobalProgress] = useState(0);
  const [transitionPreviewTick, setTransitionPreviewTick] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const customMusicInputRef = useRef<HTMLInputElement>(null);
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement }>({});

  const selectedFile = files.find(file => file.id === selectedFileId);
  const selectedStoryMusic = files.find((file) => file.type === 'audio' && file.metadata?.role === 'music');
  const visualStoryFiles = files.filter((file) =>
    mergedConfig.storyMode && (file.type === 'image' || file.type === 'video') && file.metadata?.role !== 'music'
  );

  const selectedTransition = (selectedFile?.metadata?.transitionEffect || 'fade') as StoryTransitionEffect;

  useEffect(() => {
    onFilesChange?.(files);
  }, [files, onFilesChange]);

  const generateId = () => `media_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const getMediaType = (file: File): MediaFile['type'] => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'document';
  };

  const validateFile = (file: File): string | null => {
    if (file.size > mergedConfig.maxFileSize!) {
      return `File size must be less than ${formatFileSize(mergedConfig.maxFileSize!)}`;
    }
    
    const isAccepted = mergedConfig.acceptedTypes!.some(type => {
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.slice(0, -1));
      }
      return file.type === type;
    });
    
    if (!isAccepted) {
      return `File type not supported. Accepted types: ${mergedConfig.acceptedTypes!.join(', ')}`;
    }
    
    return null;
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      handleFiles(selectedFiles);
    }
  };

  const handleFiles = (fileList: File[]) => {
    const validFiles: MediaFile[] = [];
    const errors: string[] = [];

    fileList.forEach(file => {
      if (files.length + validFiles.length >= mergedConfig.maxFiles!) {
        errors.push(`Maximum ${mergedConfig.maxFiles} files allowed`);
        return;
      }

      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
        return;
      }

      const mediaFile: MediaFile = {
        id: generateId(),
        file,
        preview: URL.createObjectURL(file),
        type: getMediaType(file),
        name: file.name,
        size: file.size,
        uploadProgress: 0,
        uploaded: false,
        filters: { ...DEFAULT_FILTERS },
        videoOptions: { ...DEFAULT_VIDEO_OPTIONS },
        metadata: {
          title: file.name.replace(/\.[^/.]+$/, ""),
          description: '',
          tags: [],
          category: '',
          visibility: 'public',
          transitionEffect: 'fade',
          role: getMediaType(file) === 'audio' ? 'music' : 'primary',
        }
      };

      validFiles.push(mediaFile);
    });

    if (errors.length > 0) {
      toast({
        title: "Upload Errors",
        description: errors.join('\n'),
        variant: "destructive"
      });
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      if (!selectedFileId || validFiles.length === 1) {
        setSelectedFileId(validFiles[0].id);
        if (mergedConfig.showFilters || mergedConfig.showMetadata) {
          setCurrentTab('edit');
        }
      }
      
      if (mergedConfig.autoUpload) {
        handleUpload();
      }
    }
  };

  const addCustomMusicFile = useCallback((musicFile: File) => {
    const error = validateFile(musicFile);
    if (error) {
      toast({
        title: 'Custom music error',
        description: `${musicFile.name}: ${error}`,
        variant: 'destructive',
      });
      return;
    }

    if (!musicFile.type.startsWith('audio/')) {
      toast({
        title: 'Invalid music file',
        description: 'Please choose an audio file for story music.',
        variant: 'destructive',
      });
      return;
    }

    const mediaFile: MediaFile = {
      id: generateId(),
      file: musicFile,
      preview: URL.createObjectURL(musicFile),
      type: 'audio',
      name: musicFile.name,
      size: musicFile.size,
      uploadProgress: 0,
      uploaded: false,
      filters: { ...DEFAULT_FILTERS },
      videoOptions: { ...DEFAULT_VIDEO_OPTIONS },
      metadata: {
        title: musicFile.name.replace(/\.[^/.]+$/, ''),
        description: '',
        tags: [],
        category: 'music',
        visibility: 'public',
        transitionEffect: 'fade',
        role: 'music',
      },
    };

    setFiles((prev) => {
      const withoutMusic = prev.filter((file) => !(file.type === 'audio' && file.metadata?.role === 'music'));
      const roomLeft = Math.max(0, (mergedConfig.maxFiles || 10) - withoutMusic.length);
      if (roomLeft < 1) {
        toast({
          title: 'Upload limit reached',
          description: `Maximum ${mergedConfig.maxFiles} files allowed. Remove one file to add custom music.`,
          variant: 'destructive',
        });
        return prev;
      }
      return [...withoutMusic, mediaFile];
    });

    toast({
      title: 'Custom music added',
      description: 'Your story now has a dedicated music track.',
    });
  }, [mergedConfig.maxFiles, toast]);

  const handleCustomMusicInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) {
      return;
    }
    addCustomMusicFile(selected);
    e.target.value = '';
  }, [addCustomMusicFile]);

  const removeCustomMusic = useCallback(() => {
    setFiles((prev) => {
      const target = prev.find((file) => file.type === 'audio' && file.metadata?.role === 'music');
      if (target) {
        URL.revokeObjectURL(target.preview);
      }
      return prev.filter((file) => !(file.type === 'audio' && file.metadata?.role === 'music'));
    });
  }, []);

  const updateFile = (fileId: string, updates: Partial<MediaFile>) => {
    setFiles(prev => prev.map(file => 
      file.id === fileId ? { ...file, ...updates } : file
    ));
  };

  const applyStoryFilterPreset = useCallback((fileId: string, presetId: string) => {
    const preset = STORY_FILTER_PRESETS.find((item) => item.id === presetId);
    if (!preset) {
      return;
    }

    updateFile(fileId, {
      filters: { ...preset.values },
    });
  }, []);

  const applyTransitionToAllStoryVisuals = useCallback((transition: StoryTransitionEffect) => {
    setFiles((prev) => prev.map((file) => {
      if (!(file.type === 'image' || file.type === 'video') || file.metadata?.role === 'music') {
        return file;
      }

      return {
        ...file,
        metadata: {
          ...file.metadata,
          transitionEffect: transition,
        },
      };
    }));
  }, []);

  const applyAutoTransitionSequence = useCallback(() => {
    const cycle: StoryTransitionEffect[] = ['fade', 'slide', 'zoom', 'flip'];
    let visualIndex = 0;

    setFiles((prev) => prev.map((file) => {
      if (!(file.type === 'image' || file.type === 'video') || file.metadata?.role === 'music') {
        return file;
      }

      const transitionEffect = cycle[visualIndex % cycle.length];
      visualIndex += 1;
      return {
        ...file,
        metadata: {
          ...file.metadata,
          transitionEffect,
        },
      };
    }));

    toast({
      title: 'Transitions auto-arranged',
      description: 'Applied a balanced transition sequence across selected story visuals.',
    });
  }, [toast]);

  const moveFile = useCallback((fileId: string, direction: 'up' | 'down') => {
    setFiles((prev) => {
      const index = prev.findIndex((file) => file.id === fileId);
      if (index === -1) {
        return prev;
      }

      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= prev.length) {
        return prev;
      }

      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(nextIndex, 0, moved);
      return next;
    });
  }, []);

  const getTransitionPreviewStyle = useCallback((transition: StoryTransitionEffect): React.CSSProperties => {
    switch (transition) {
      case 'slide':
        return { animation: 'storyTransitionSlide 560ms cubic-bezier(0.22, 1, 0.36, 1) both' };
      case 'zoom':
        return { animation: 'storyTransitionZoom 560ms cubic-bezier(0.22, 1, 0.36, 1) both' };
      case 'flip':
        return { animation: 'storyTransitionFlip 650ms cubic-bezier(0.22, 1, 0.36, 1) both', transformStyle: 'preserve-3d' };
      case 'fade':
      default:
        return { animation: 'storyTransitionFade 520ms ease-out both' };
    }
  }, []);

  const removeFile = (fileId: string) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
    
    if (selectedFileId === fileId) {
      setSelectedFileId(null);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    setGlobalProgress(0);
    
    try {
      // Simulate upload progress for each file
      const totalFiles = files.length;
      let completedFiles = 0;
      
      for (const file of files) {
        if (!file.uploaded) {
          // Simulate individual file upload progress
          for (let progress = 0; progress <= 100; progress += 10) {
            updateFile(file.id, { uploadProgress: progress });
            setGlobalProgress(((completedFiles * 100 + progress) / totalFiles));
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          
          updateFile(file.id, { uploaded: true, uploadProgress: 100 });
          completedFiles++;
        }
      }
      
      await onUpload(files);
      
      toast({
        title: "Upload Complete",
        description: `Successfully uploaded ${files.length} file(s)`
      });
      
      setTimeout(() => {
        setUploading(false);
        onClose();
        resetState();
      }, 500);
      
    } catch (error) {
      setUploading(false);
      toast({
        title: "Upload Failed",
        description: "There was an error uploading your files. Please try again.",
        variant: "destructive"
      });
    }
  };

  const resetState = () => {
    files.forEach(file => URL.revokeObjectURL(file.preview));
    setFiles([]);
    setSelectedFileId(null);
    setCurrentTab('upload');
    setGlobalProgress(0);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: MediaFile['type']) => {
    switch (type) {
      case 'image': return <ImageIcon className="w-5 h-5" />;
      case 'video': return <Video className="w-5 h-5" />;
      case 'audio': return <Music className="w-5 h-5" />;
      default: return <File className="w-5 h-5" />;
    }
  };

  const getFilterStyle = (filters: MediaFile['filters']) => ({
    filter: `brightness(${filters?.brightness || 100}%) contrast(${filters?.contrast || 100}%) saturate(${filters?.saturation || 100}%) blur(${filters?.blur || 0}px) hue-rotate(${filters?.hue || 0}deg) sepia(${filters?.sepia || 0}%) grayscale(${filters?.grayscale || 0}%)`
  });

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(
        "max-w-6xl max-h-[90vh] overflow-hidden p-0",
        mergedConfig.mode === 'compact' && "max-w-2xl",
        mergedConfig.mode === 'inline' && "max-w-4xl"
      )}>
        <style>{`@keyframes storyTransitionFade {0% {opacity: 0.4;} 100% {opacity: 1;}}
          @keyframes storyTransitionSlide {0% {opacity: 0.4; transform: translateX(26px);} 100% {opacity: 1; transform: translateX(0);}}
          @keyframes storyTransitionZoom {0% {opacity: 0.45; transform: scale(1.08);} 100% {opacity: 1; transform: scale(1);}}
          @keyframes storyTransitionFlip {0% {opacity: 0.45; transform: perspective(800px) rotateY(16deg) scale(0.98);} 100% {opacity: 1; transform: perspective(800px) rotateY(0deg) scale(1);}}
        `}</style>
        <DialogHeader className="p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50">
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {mergedConfig.title || 'Media Uploader'}
          </DialogTitle>
          <DialogDescription>
            {mergedConfig.description || 'Upload and manage your media files with advanced editing capabilities'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <Tabs value={currentTab} onValueChange={setCurrentTab} className="h-full flex flex-col">
            <div className="px-6 pt-4">
              <TabsList className="grid w-full grid-cols-4 bg-gray-100 p-1 rounded-xl">
                <TabsTrigger value="upload" className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Upload
                </TabsTrigger>
                <TabsTrigger value="manage" disabled={files.length === 0} className="flex items-center gap-2">
                  <Folder className="w-4 h-4" />
                  Manage ({files.length})
                </TabsTrigger>
                <TabsTrigger value="edit" disabled={!selectedFile} className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Edit
                </TabsTrigger>
                <TabsTrigger value="preview" disabled={files.length === 0} className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Preview
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <TabsContent value="upload" className="space-y-6 mt-0">
                <div
                  className={cn(
                    "border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300",
                    dragActive 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  )}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <Cloud className="w-10 h-10 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Drop your files here</h3>
                      <p className="text-gray-600 mb-4">
                        {mergedConfig.allowMultiple 
                          ? `Upload up to ${mergedConfig.maxFiles} files` 
                          : 'Upload a single file'
                        } (Max {formatFileSize(mergedConfig.maxFileSize!)})
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        Supported: {mergedConfig.acceptedTypes!.join(', ')}
                      </p>
                      <Button 
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Choose Files
                      </Button>
                    </div>
                  </div>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={mergedConfig.acceptedTypes!.join(',')}
                  multiple={mergedConfig.allowMultiple}
                  onChange={handleFileInput}
                  className="hidden"
                />
                <input
                  ref={customMusicInputRef}
                  type="file"
                  accept="audio/*"
                  multiple={false}
                  onChange={handleCustomMusicInput}
                  className="hidden"
                />

                {/* Quick Upload Options */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-blue-200" 
                        onClick={() => fileInputRef.current?.click()}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                        <Camera className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Photos & Images</h4>
                        <p className="text-sm text-gray-600">JPG, PNG, GIF, WebP</p>
                      </div>
                    </div>
                  </Card>
                  
                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-blue-200" 
                        onClick={() => fileInputRef.current?.click()}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Video className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Videos</h4>
                        <p className="text-sm text-gray-600">MP4, MOV, AVI, WebM</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-blue-200" 
                        onClick={() => fileInputRef.current?.click()}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Zap className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">AI Enhanced</h4>
                        <p className="text-sm text-gray-600">Auto-optimize quality</p>
                      </div>
                    </div>
                  </Card>
                </div>

                {mergedConfig.storyMode && (
                  <Card className="border-blue-100 bg-blue-50/60">
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h4 className="font-semibold text-slate-900">Story Studio Tools</h4>
                          <p className="text-sm text-slate-600">Add custom music, then open Edit to apply effects and transitions per photo.</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => customMusicInputRef.current?.click()}
                          >
                            <Music className="w-4 h-4 mr-2" />
                            {selectedStoryMusic ? 'Replace Music' : 'Add Custom Music'}
                          </Button>
                          {selectedStoryMusic && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={removeCustomMusic}
                              className="text-red-600 hover:text-red-700"
                            >
                              Remove
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setCurrentTab('edit')}
                            disabled={files.length === 0}
                          >
                            <Palette className="w-4 h-4 mr-2" />
                            Effects & Transitions
                          </Button>
                        </div>
                      </div>
                      {selectedStoryMusic && (
                        <p className="text-xs text-slate-600 mt-2">Current music: {selectedStoryMusic.name}</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Current Upload Queue */}
                {files.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>Upload Queue ({files.length} files)</span>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setCurrentTab('manage')}
                          >
                            <Settings className="w-4 h-4 mr-2" />
                            Manage
                          </Button>
                          {!mergedConfig.autoUpload && (
                            <Button 
                              onClick={handleUpload}
                              disabled={uploading}
                              className="bg-gradient-to-r from-green-500 to-blue-500"
                            >
                              {uploading ? (
                                <>
                                  <div className="w-4 h-4 mr-2 animate-spin border-2 border-white border-t-transparent rounded-full" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Check className="w-4 h-4 mr-2" />
                                  Upload All
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {files.slice(0, 3).map(file => (
                          <div key={file.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                            <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                              {getFileIcon(file.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{file.name}</p>
                              <p className="text-sm text-gray-600">{formatFileSize(file.size)}</p>
                            </div>
                            {file.uploaded ? (
                              <Badge className="bg-green-100 text-green-800">Uploaded</Badge>
                            ) : file.uploadProgress > 0 ? (
                              <div className="flex items-center gap-2">
                                <Progress value={file.uploadProgress} className="w-16" />
                                <span className="text-xs text-gray-600">{file.uploadProgress}%</span>
                              </div>
                            ) : (
                              <Badge variant="outline">Pending</Badge>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => removeFile(file.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        {files.length > 3 && (
                          <p className="text-sm text-gray-600 text-center py-2">
                            And {files.length - 3} more files...
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="manage" className="space-y-6 mt-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">File Management</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                    >
                      <Grid className="w-4 h-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                    >
                      <List className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {mergedConfig.storyMode && visualStoryFiles.length > 0 && (
                  <Card className="border-blue-100 bg-blue-50/60">
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="font-medium text-slate-900">Story Sequence Builder</p>
                          <p className="text-sm text-slate-600">Reorder photos/videos and auto-assign transitions for smoother playback.</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={applyAutoTransitionSequence}>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Auto Arrange Transitions
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className={cn(
                  viewMode === 'grid' 
                    ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" 
                    : "space-y-2"
                )}>
                  {files.map(file => (
                    <Card 
                      key={file.id} 
                      className={cn(
                        "cursor-pointer transition-all hover:shadow-md",
                        selectedFileId === file.id && "ring-2 ring-blue-500",
                        viewMode === 'list' && "flex items-center"
                      )}
                      onClick={() => setSelectedFileId(file.id)}
                    >
                      {viewMode === 'grid' ? (
                        <CardContent className="p-3">
                          <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 mb-3">
                            {file.type === 'image' ? (
                              <img 
                                src={file.preview} 
                                alt={file.name}
                                className="w-full h-full object-cover"
                                style={getFilterStyle(file.filters)}
                              />
                            ) : file.type === 'video' ? (
                              <video 
                                src={file.preview}
                                className="w-full h-full object-cover"
                                style={getFilterStyle(file.filters)}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                {getFileIcon(file.type)}
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <p className="font-medium truncate text-sm">{file.name}</p>
                            <div className="flex items-center justify-between text-xs text-gray-600">
                              <span>{formatFileSize(file.size)}</span>
                              {file.uploaded ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">Done</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">Pending</Badge>
                              )}
                            </div>
                            {mergedConfig.storyMode && file.type !== 'audio' && (
                              <div className="flex items-center justify-between text-[11px]">
                                <span className="text-slate-500">Transition</span>
                                <Badge variant="secondary" className="capitalize">{file.metadata?.transitionEffect || 'fade'}</Badge>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      ) : (
                        <CardContent className="p-4 flex items-center gap-4">
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                            {file.type === 'image' ? (
                              <img 
                                src={file.preview} 
                                alt={file.name}
                                className="w-full h-full object-cover"
                                style={getFilterStyle(file.filters)}
                              />
                            ) : file.type === 'video' ? (
                              <video 
                                src={file.preview}
                                className="w-full h-full object-cover"
                                style={getFilterStyle(file.filters)}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                {getFileIcon(file.type)}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{file.name}</p>
                            <p className="text-sm text-gray-600">{formatFileSize(file.size)} • {file.type}</p>
                            {file.metadata?.title && (
                              <p className="text-xs text-gray-500 truncate">{file.metadata.title}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {file.uploaded ? (
                              <Badge className="bg-green-100 text-green-800">Uploaded</Badge>
                            ) : file.uploadProgress > 0 ? (
                              <Progress value={file.uploadProgress} className="w-20" />
                            ) : (
                              <Badge variant="outline">Pending</Badge>
                            )}
                            {mergedConfig.storyMode && file.type !== 'audio' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); moveFile(file.id, 'up'); }}
                                  disabled={files.findIndex((item) => item.id === file.id) === 0}
                                  aria-label="Move file up"
                                >
                                  ↑
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); moveFile(file.id, 'down'); }}
                                  disabled={files.findIndex((item) => item.id === file.id) === files.length - 1}
                                  aria-label="Move file down"
                                >
                                  ↓
                                </Button>
                              </>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {e.stopPropagation(); removeFile(file.id);}}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>

                {selectedFile && (
                  <div className="flex gap-3 justify-center pt-4 border-t">
                    <Button 
                      variant="outline"
                      onClick={() => setCurrentTab('edit')}
                    >
                      <Palette className="w-4 h-4 mr-2" />
                      Edit Selected
                    </Button>
                    <Button 
                      onClick={() => setCurrentTab('preview')}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="edit" className="space-y-6 mt-0">
                {selectedFile && (
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Preview Section */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Preview</h3>
                      <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border">
                        {selectedFile.type === 'image' ? (
                          <div
                            key={`${selectedFile.id}-${selectedTransition}-${transitionPreviewTick}`}
                            className="w-full h-full"
                            style={mergedConfig.storyMode ? getTransitionPreviewStyle(selectedTransition) : undefined}
                          >
                            <img
                              src={selectedFile.preview}
                              alt="Preview"
                              className="w-full h-full object-cover"
                              style={getFilterStyle(selectedFile.filters)}
                            />
                          </div>
                        ) : selectedFile.type === 'video' ? (
                          <div
                            key={`${selectedFile.id}-${selectedTransition}-${transitionPreviewTick}`}
                            className="w-full h-full"
                            style={mergedConfig.storyMode ? getTransitionPreviewStyle(selectedTransition) : undefined}
                          >
                            <video
                              ref={(ref) => {
                                if (ref) videoRefs.current[selectedFile.id] = ref;
                              }}
                              src={selectedFile.preview}
                              className="w-full h-full object-cover"
                              autoPlay={selectedFile.videoOptions?.autoplay}
                              muted={selectedFile.videoOptions?.muted}
                              loop={selectedFile.videoOptions?.loop}
                              controls={selectedFile.videoOptions?.controls}
                              style={getFilterStyle(selectedFile.filters)}
                            />
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {getFileIcon(selectedFile.type)}
                            <div className="ml-4">
                              <p className="font-medium">{selectedFile.name}</p>
                              <p className="text-sm text-gray-600">{formatFileSize(selectedFile.size)}</p>
                            </div>
                          </div>
                        )}
                      </div>
                      {mergedConfig.storyMode && selectedFile.type !== 'audio' && (
                        <div className="flex items-center justify-between rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-slate-700">
                          <span className="capitalize">Preview transition: {selectedTransition}</span>
                          <Button variant="ghost" size="sm" onClick={() => setTransitionPreviewTick((prev) => prev + 1)}>
                            Replay
                          </Button>
                        </div>
                      )}
                      
                      {/* File Info */}
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3 mb-3">
                            {getFileIcon(selectedFile.type)}
                            <div>
                              <p className="font-medium">{selectedFile.name}</p>
                              <p className="text-sm text-gray-600">{formatFileSize(selectedFile.size)}</p>
                            </div>
                            <Badge variant="secondary" className="ml-auto">
                              {selectedFile.file.type.split('/')[1].toUpperCase()}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Edit Controls */}
                    <div className="space-y-6">
                      <Tabs defaultValue="filters">
                        <TabsList className="grid w-full grid-cols-3">
                          <TabsTrigger value="filters">Filters</TabsTrigger>
                          <TabsTrigger value="metadata">Details</TabsTrigger>
                          <TabsTrigger value="settings">Settings</TabsTrigger>
                        </TabsList>

                        <TabsContent value="filters" className="space-y-4 mt-4">
                          <h4 className="font-medium">Visual Adjustments</h4>

                          {mergedConfig.storyMode && (selectedFile.type === 'image' || selectedFile.type === 'video') && (
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Quick Effect Presets</label>
                              <div className="grid grid-cols-2 gap-2">
                                {STORY_FILTER_PRESETS.map((preset) => (
                                  <Button
                                    key={preset.id}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => applyStoryFilterPreset(selectedFile.id, preset.id)}
                                  >
                                    {preset.label}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {(selectedFile.type === 'image' || selectedFile.type === 'video') && (
                            <>
                              <div>
                                <label className="text-sm font-medium flex items-center justify-between">
                                  Brightness
                                  <span className="text-gray-500">{selectedFile.filters?.brightness}%</span>
                                </label>
                                <Slider
                                  value={[selectedFile.filters?.brightness || 100]}
                                  onValueChange={([value]) => updateFile(selectedFile.id, {
                                    filters: { ...selectedFile.filters!, brightness: value }
                                  })}
                                  min={0}
                                  max={200}
                                  step={1}
                                  className="mt-2"
                                />
                              </div>

                              <div>
                                <label className="text-sm font-medium flex items-center justify-between">
                                  Contrast
                                  <span className="text-gray-500">{selectedFile.filters?.contrast}%</span>
                                </label>
                                <Slider
                                  value={[selectedFile.filters?.contrast || 100]}
                                  onValueChange={([value]) => updateFile(selectedFile.id, {
                                    filters: { ...selectedFile.filters!, contrast: value }
                                  })}
                                  min={0}
                                  max={200}
                                  step={1}
                                  className="mt-2"
                                />
                              </div>

                              <div>
                                <label className="text-sm font-medium flex items-center justify-between">
                                  Saturation
                                  <span className="text-gray-500">{selectedFile.filters?.saturation}%</span>
                                </label>
                                <Slider
                                  value={[selectedFile.filters?.saturation || 100]}
                                  onValueChange={([value]) => updateFile(selectedFile.id, {
                                    filters: { ...selectedFile.filters!, saturation: value }
                                  })}
                                  min={0}
                                  max={200}
                                  step={1}
                                  className="mt-2"
                                />
                              </div>

                              <div>
                                <label className="text-sm font-medium flex items-center justify-between">
                                  Blur
                                  <span className="text-gray-500">{selectedFile.filters?.blur}px</span>
                                </label>
                                <Slider
                                  value={[selectedFile.filters?.blur || 0]}
                                  onValueChange={([value]) => updateFile(selectedFile.id, {
                                    filters: { ...selectedFile.filters!, blur: value }
                                  })}
                                  min={0}
                                  max={10}
                                  step={0.1}
                                  className="mt-2"
                                />
                              </div>

                              <div>
                                <label className="text-sm font-medium flex items-center justify-between">
                                  Hue Rotate
                                  <span className="text-gray-500">{selectedFile.filters?.hue}°</span>
                                </label>
                                <Slider
                                  value={[selectedFile.filters?.hue || 0]}
                                  onValueChange={([value]) => updateFile(selectedFile.id, {
                                    filters: { ...selectedFile.filters!, hue: value }
                                  })}
                                  min={-180}
                                  max={180}
                                  step={1}
                                  className="mt-2"
                                />
                              </div>

                              <div>
                                <label className="text-sm font-medium flex items-center justify-between">
                                  Sepia
                                  <span className="text-gray-500">{selectedFile.filters?.sepia}%</span>
                                </label>
                                <Slider
                                  value={[selectedFile.filters?.sepia || 0]}
                                  onValueChange={([value]) => updateFile(selectedFile.id, {
                                    filters: { ...selectedFile.filters!, sepia: value }
                                  })}
                                  min={0}
                                  max={100}
                                  step={1}
                                  className="mt-2"
                                />
                              </div>

                              <div>
                                <label className="text-sm font-medium flex items-center justify-between">
                                  Grayscale
                                  <span className="text-gray-500">{selectedFile.filters?.grayscale}%</span>
                                </label>
                                <Slider
                                  value={[selectedFile.filters?.grayscale || 0]}
                                  onValueChange={([value]) => updateFile(selectedFile.id, {
                                    filters: { ...selectedFile.filters!, grayscale: value }
                                  })}
                                  min={0}
                                  max={100}
                                  step={1}
                                  className="mt-2"
                                />
                              </div>
                            </>
                          )}

                          <Button 
                            variant="outline" 
                            onClick={() => updateFile(selectedFile.id, { filters: { ...DEFAULT_FILTERS } })}
                            className="w-full"
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Reset Filters
                          </Button>
                        </TabsContent>

                        <TabsContent value="metadata" className="space-y-4 mt-4">
                          <h4 className="font-medium">File Details</h4>

                          {mergedConfig.storyMode && selectedFile.type !== 'audio' && (
                            <Card className="border-blue-100 bg-blue-50/60">
                              <CardContent className="p-4 space-y-4">
                                <div>
                                  <label className="text-sm font-medium">Transition</label>
                                  <div className="mt-2 grid grid-cols-2 gap-2">
                                    {STORY_TRANSITION_OPTIONS.map((option) => {
                                      const active = (selectedFile.metadata?.transitionEffect || 'fade') === option.value;
                                      return (
                                        <button
                                          key={option.value}
                                          type="button"
                                          onClick={() => {
                                            updateFile(selectedFile.id, {
                                              metadata: { ...selectedFile.metadata!, transitionEffect: option.value },
                                            });
                                            setTransitionPreviewTick((prev) => prev + 1);
                                          }}
                                          className={cn(
                                            'rounded-lg border p-2 text-left transition-colors',
                                            active ? 'border-blue-400 bg-white shadow-sm' : 'border-blue-100 bg-white/80 hover:border-blue-300'
                                          )}
                                        >
                                          <p className="text-sm font-semibold">{option.label}</p>
                                          <p className="text-[11px] text-slate-600">{option.description}</p>
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {visualStoryFiles.length > 1 && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="mt-3"
                                      onClick={() => applyTransitionToAllStoryVisuals(selectedTransition)}
                                    >
                                      Apply {selectedTransition} To All Photos
                                    </Button>
                                  )}
                                </div>

                                <div className="space-y-2">
                                  <label className="text-sm font-medium">Custom Music</label>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => customMusicInputRef.current?.click()}
                                    >
                                      <Music className="w-4 h-4 mr-2" />
                                      {selectedStoryMusic ? 'Replace Music' : 'Pick Music'}
                                    </Button>
                                    {selectedStoryMusic && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={removeCustomMusic}
                                        className="text-red-600 hover:text-red-700"
                                      >
                                        Remove
                                      </Button>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600">
                                    {selectedStoryMusic ? `Using: ${selectedStoryMusic.name}` : 'Optional track that plays with image/video stories.'}
                                  </p>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                          
                          <div>
                            <label className="text-sm font-medium">Title</label>
                            <Input
                              value={selectedFile.metadata?.title || ''}
                              onChange={(e) => updateFile(selectedFile.id, {
                                metadata: { ...selectedFile.metadata!, title: e.target.value }
                              })}
                              placeholder="Enter title..."
                              className="mt-1"
                            />
                          </div>

                          <div>
                            <label className="text-sm font-medium">Description</label>
                            <Textarea
                              value={selectedFile.metadata?.description || ''}
                              onChange={(e) => updateFile(selectedFile.id, {
                                metadata: { ...selectedFile.metadata!, description: e.target.value }
                              })}
                              placeholder="Enter description..."
                              className="mt-1"
                              rows={3}
                            />
                          </div>

                          <div>
                            <label className="text-sm font-medium">Category</label>
                            <Select
                              value={selectedFile.metadata?.category || ''}
                              onValueChange={(value) => updateFile(selectedFile.id, {
                                metadata: { ...selectedFile.metadata!, category: value }
                              })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select category..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="personal">Personal</SelectItem>
                                <SelectItem value="work">Work</SelectItem>
                                <SelectItem value="social">Social</SelectItem>
                                <SelectItem value="creative">Creative</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <label className="text-sm font-medium">Visibility</label>
                            <Select
                              value={selectedFile.metadata?.visibility || 'public'}
                              onValueChange={(value: 'public' | 'private' | 'friends') => updateFile(selectedFile.id, {
                                metadata: { ...selectedFile.metadata!, visibility: value }
                              })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="public">Public</SelectItem>
                                <SelectItem value="friends">Friends Only</SelectItem>
                                <SelectItem value="private">Private</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <label className="text-sm font-medium">Tags</label>
                            <Input
                              value={selectedFile.metadata?.tags?.join(', ') || ''}
                              onChange={(e) => updateFile(selectedFile.id, {
                                metadata: { 
                                  ...selectedFile.metadata!, 
                                  tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                                }
                              })}
                              placeholder="tag1, tag2, tag3..."
                              className="mt-1"
                            />
                            <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
                          </div>
                        </TabsContent>

                        <TabsContent value="settings" className="space-y-4 mt-4">
                          <h4 className="font-medium">Media Settings</h4>

                          {selectedFile.type === 'video' && (
                            <>
                              <div className="flex items-center justify-between">
                                <label className="text-sm font-medium">Autoplay</label>
                                <Switch
                                  checked={selectedFile.videoOptions?.autoplay || false}
                                  onCheckedChange={(checked) => updateFile(selectedFile.id, {
                                    videoOptions: { ...selectedFile.videoOptions!, autoplay: checked }
                                  })}
                                />
                              </div>

                              <div className="flex items-center justify-between">
                                <label className="text-sm font-medium">Muted</label>
                                <Switch
                                  checked={selectedFile.videoOptions?.muted || true}
                                  onCheckedChange={(checked) => updateFile(selectedFile.id, {
                                    videoOptions: { ...selectedFile.videoOptions!, muted: checked }
                                  })}
                                />
                              </div>

                              <div className="flex items-center justify-between">
                                <label className="text-sm font-medium">Loop</label>
                                <Switch
                                  checked={selectedFile.videoOptions?.loop || false}
                                  onCheckedChange={(checked) => updateFile(selectedFile.id, {
                                    videoOptions: { ...selectedFile.videoOptions!, loop: checked }
                                  })}
                                />
                              </div>

                              <div className="flex items-center justify-between">
                                <label className="text-sm font-medium">Show Controls</label>
                                <Switch
                                  checked={selectedFile.videoOptions?.controls !== false}
                                  onCheckedChange={(checked) => updateFile(selectedFile.id, {
                                    videoOptions: { ...selectedFile.videoOptions!, controls: checked }
                                  })}
                                />
                              </div>
                            </>
                          )}

                          <div>
                            <label className="text-sm font-medium">Quality</label>
                            <Select
                              value={mergedConfig.quality}
                              onValueChange={(value) => console.log('Quality changed:', value)}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Low (Faster upload)</SelectItem>
                                <SelectItem value="medium">Medium (Balanced)</SelectItem>
                                <SelectItem value="high">High (Better quality)</SelectItem>
                                <SelectItem value="original">Original (No compression)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </TabsContent>
                      </Tabs>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="preview" className="space-y-6 mt-0">
                <div className="text-center space-y-4">
                  <h3 className="text-lg font-semibold">Final Preview</h3>
                  <p className="text-gray-600">Review all your files before uploading</p>
                </div>

                <div className="grid gap-6">
                  {files.map(file => (
                    <Card key={file.id} className="overflow-hidden">
                      <div className="flex items-start gap-4 p-4">
                        <div className="w-32 h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          {file.type === 'image' ? (
                            <img
                              src={file.preview}
                              alt={file.name}
                              className="w-full h-full object-cover"
                              style={getFilterStyle(file.filters)}
                            />
                          ) : file.type === 'video' ? (
                            <video
                              src={file.preview}
                              className="w-full h-full object-cover"
                              style={getFilterStyle(file.filters)}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              {getFileIcon(file.type)}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold truncate">{file.metadata?.title || file.name}</h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {formatFileSize(file.size)} • {file.type} • {file.metadata?.visibility}
                              </p>
                              {file.metadata?.description && (
                                <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                                  {file.metadata.description}
                                </p>
                              )}
                              {file.metadata?.tags && file.metadata.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {file.metadata.tags.map(tag => (
                                    <Badge key={tag} variant="outline" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {file.uploaded ? (
                                <Badge className="bg-green-100 text-green-800">Uploaded</Badge>
                              ) : (
                                <Badge variant="outline">Ready</Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedFileId(file.id);
                                  setCurrentTab('edit');
                                }}
                              >
                                <Settings className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </div>

            {/* Global Upload Progress */}
            {uploading && (
              <div className="border-t p-4 bg-gray-50">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Uploading files...</span>
                    <span>{Math.round(globalProgress)}%</span>
                  </div>
                  <Progress value={globalProgress} className="w-full" />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {!uploading && files.length > 0 && (
              <div className="border-t p-4 bg-gray-50 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {files.length} file(s) ready to upload
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={resetState}>
                    <X className="w-4 h-4 mr-2" />
                    Clear All
                  </Button>
                  <Button 
                    onClick={handleUpload}
                    className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Upload All Files
                  </Button>
                </div>
              </div>
            )}
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
